// ============================================
// FILE: src/components/ProfileScreen.jsx
// ============================================

import React, { useState } from 'react';
import { User, MapPin, Shield, Heart, Package, Bell, LogOut, ChevronRight, MessageCircle, Smartphone, ArrowLeft, Palette, SunMedium, Trees, MoonStar, Check, QrCode, Copy, Gift, Users } from 'lucide-react';
import PWAScreen from './PWAScreen';
import QRCodeScreen from './QRCodeScreen';
import { getProductImage } from '../utils/imageHelpers';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(date.getDate()).padStart(2,'0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const ProfileScreen = ({
  userProfile,
  setUserProfile,
  handleLogout,
  subscription,
  setSubscription,
  twoFactorEnabled,
  setTwoFactorEnabled,
  showSecuritySettings,
  setShowSecuritySettings,
  loginHistory,
  securityLogs,
  orders,
  favorites,
  setFavorites,
  setShowFavorites,
  setShowNotificationsModal,
  setShowPaymentMethods,
  setShowEditAddress,
  setShowEditProfile,  // Add this line
  addSecurityLog,
  deliveryAddresses,
  handleAddAddress,
  handleUpdateAddress,
  handleDeleteAddress,
  handleSetDefaultAddress,
  setShowAddAddress,
  editingAddress,        // Add this
  setEditingAddress,      // Add this
  allSubscriptions = [],
  handlePauseSubscription,
  handleResumeSubscription,
  handleCancelSubscription,
  handleModifySubscription,
  handleReactivateSubscription,
  appSettings,
  appTheme,
  onChangeTheme,
  loading,
  setActiveTab,
  goBack,
  walletBalance,
  onShowWalletTopup,
  onShowWalletTransactions,
  referralInfo = null,
  onApplyReferralCode,
  isDesktop = false,
}) => {
  const [showPWAScreen, setShowPWAScreen] = useState(false);
  const [showQRScreen, setShowQRScreen] = useState(false);
  const [referralCodeCopied, setReferralCodeCopied] = useState(false);
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralMsg, setReferralMsg] = useState(null); // { type: 'success'|'error', text }

  const copyReferralCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => {
      setReferralCodeCopied(true);
      setTimeout(() => setReferralCodeCopied(false), 2000);
    });
  };

  const handleApplyReferral = async () => {
    if (!referralInput.trim() || !onApplyReferralCode) return;
    setApplyingReferral(true);
    setReferralMsg(null);
    try {
      const resp = await onApplyReferralCode(referralInput.trim().toUpperCase());
      if (resp && resp.success) {
        setReferralMsg({ type: 'success', text: resp.data?.message || 'Referral applied!' });
        setReferralInput('');
      } else {
        setReferralMsg({ type: 'error', text: resp?.data?.message || 'Failed to apply code.' });
      }
    } catch (e) {
      setReferralMsg({ type: 'error', text: 'Failed to apply referral code.' });
    } finally {
      setApplyingReferral(false);
    }
  };

  const themeOptions = [
        {
          id: 'blue',
          label: 'Blue',
          description: 'Modern blue gradients and accents.',
          icon: Palette,
          previewClass: 'from-blue-500 via-blue-400 to-blue-300',
          previewIconClass: 'text-white',
        },
        {
          id: 'white',
          label: 'White',
          description: 'Minimal white and light gray surfaces.',
          icon: Palette,
          previewClass: 'from-white via-gray-100 to-gray-200',
          previewIconClass: 'text-gray-700',
        },
        {
          id: 'sunrise',
          label: 'Sunrise',
          description: 'Very light amber, orange, and sky tones.',
          icon: SunMedium,
          previewClass: 'from-amber-200 via-orange-100 to-sky-300',
          previewIconClass: 'text-white',
        },
        {
          id: 'grove',
          label: 'Grove',
          description: 'Fresh mint tones with deep evergreen accents.',
          icon: Trees,
          previewClass: 'from-emerald-300 via-lime-100 to-teal-300',
          previewIconClass: 'text-white',
        },
        {
          id: 'midnight',
          label: 'Midnight',
          description: 'Dark navy panels with electric blue highlights.',
          icon: MoonStar,
          previewClass: 'from-slate-950 via-slate-800 to-indigo-600',
          previewIconClass: 'text-slate-100',
        },
        {
          id: 'pulse',
          label: 'Pulse',
          description: 'Forest green palette with soft botanical surfaces.',
          icon: Palette,
          previewClass: 'from-[#337639] via-[#4BAB40] to-[#CFE0CF]',
          previewIconClass: 'text-white',
        },
  ];

  const renderSubCard = (sub) => {
    const statusLower = (sub.status || '').toLowerCase();
    const prod        = sub.product || {};
    const mrp         = parseFloat(prod.price        || sub.price        || 0);
    const salePrice   = parseFloat(prod.sale_price   || sub.salePrice    || mrp);
    const subPrice    = parseFloat(prod.subscription_price || sub.subscriptionPrice || salePrice);
    const discount    = sub.plan?.discount || sub.planDiscount || sub.discount || 0;
    const perDelivery = subPrice > 0 ? subPrice * (1 - discount / 100) : 0;
    const freq        = sub.planId || sub.frequency || '';
    const deliveries  = freq === 'daily' ? 30 : freq === 'alternate' ? 15 : 4;
    const totalCost   = perDelivery * deliveries;
    const totalSaving = mrp > 0 ? (mrp * deliveries) - totalCost : 0;

    const statusColors = {
      active:    'bg-green-100 text-green-700',
      paused:    'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-600',
      canceled:  'bg-red-100 text-red-600',
      expired:   'bg-gray-100 text-gray-500',
    };
    const statusCls = statusColors[statusLower] || 'bg-gray-100 text-gray-500';

    return (
      <div key={sub.id || sub.subscription_number} className="bg-white rounded-xl shadow-sm overflow-hidden mb-3">
        {/* Header — mirrors order card header */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 border-b border-green-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-gray-800 text-sm">
                {sub.subscription_number || (sub.id ? `Subscription #${sub.id}` : 'Subscription')}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {sub.plan?.name || sub.planName || sub.frequency || '—'} plan
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                📅 Started: {fmtDate(sub.startDate || sub.created_at)}
              </p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusCls}`}>
              {statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {/* Product row */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0">{getProductImage(prod, 'large')}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">
                {prod.name || sub.productName || 'Subscription'}
              </p>
              {prod.description && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{prod.description}</p>
              )}
            </div>
          </div>

          {/* Pricing row — mirrors order totals */}
          {mrp > 0 && (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 mb-3 text-sm">
              <div className="flex justify-between px-3 py-2">
                <span className="text-gray-500">MRP / delivery</span>
                <span className="text-gray-400 line-through">₹{mrp.toFixed(2)}</span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-gray-500">Subscription price</span>
                <span className="font-semibold text-blue-600">₹{(perDelivery > 0 ? perDelivery : salePrice).toFixed(2)}</span>
              </div>
              {totalSaving > 0 && (
                <div className="flex justify-between px-3 py-2 bg-green-50">
                  <span className="text-green-700 font-medium">You save ({deliveries} deliveries)</span>
                  <span className="font-bold text-green-600">₹{totalSaving.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons — mirrors order actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {statusLower === 'cancelled' || statusLower === 'canceled' ? (
              <>
                <button onClick={() => handleReactivateSubscription && handleReactivateSubscription(sub.id)} className="flex-1 text-blue-600 text-xs font-medium bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 transition-all">Reactivate</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-gray-600 text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            ) : statusLower === 'paused' || statusLower === 'inactive' ? (
              <>
                <button onClick={() => handleResumeSubscription && handleResumeSubscription(sub.id)} className="flex-1 text-green-600 text-xs font-medium bg-green-50 px-3 py-2 rounded-lg hover:bg-green-100 transition-all">Resume</button>
                <button onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)} className="flex-1 text-red-600 text-xs font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all">Cancel</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-gray-600 text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            ) : (
              <>
                <button onClick={() => handlePauseSubscription && handlePauseSubscription(sub.id)} className="flex-1 text-orange-600 text-xs font-medium bg-orange-50 px-3 py-2 rounded-lg hover:bg-orange-100 transition-all">Pause</button>
                <button onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)} className="flex-1 text-red-600 text-xs font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all">Cancel</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-gray-600 text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (showPWAScreen) {
    return <PWAScreen onBack={() => setShowPWAScreen(false)} appTheme={appTheme} />;
  }

  if (showQRScreen) {
    return <QRCodeScreen onBack={() => setShowQRScreen(false)} appTheme={appTheme} />;
  }

  return (
    <div className="pb-20 min-h-screen" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      <div
        className={`p-6 ${isDesktop ? 'rounded-3xl' : 'rounded-b-3xl'}`}
        style={appTheme === 'white' ? { background: '#fff', color: '#222' } : { background: 'var(--header-gradient)', color: 'var(--text-light)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => goBack ? goBack() : setActiveTab('home')}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
              title="Go Back"
              style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
            >
              <ArrowLeft size={22} />
            </button>
            <h2 className="text-lg font-semibold">My Profile</h2>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all backdrop-blur-sm"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
        <div className="flex items-center">
          <div className="rounded-full p-1 mr-4" style={{ background: 'var(--primary-bg-light)' }}>
            {userProfile.profilePic ? (
              <img 
                src={userProfile.profilePic} 
                alt="Profile" 
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'var(--primary-bg)' }}>
                <User size={32} style={{ color: 'var(--primary)' }} />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-light)' }}>{userProfile.name || 'User'}</h1>
            <p style={{ color: 'var(--accent-light)' }}>{userProfile.email || 'user@email.com'}</p>
            <button 
              onClick={() => setShowEditProfile(true)}
              className="text-sm mt-1 hover:underline"
              style={{ color: 'var(--accent-light)' }}
            >
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      <div className="p-4" style={{ color: 'var(--text-main)' }}>
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className="rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Package size={24} style={{ color: 'var(--primary)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{orders ? orders.length : 0}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Orders</p>
          </button>

          <button
            onClick={() => setShowFavorites(true)}
            className="rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Heart size={24} style={{ color: 'var(--danger)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{favorites ? favorites.length : 0}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Favorites</p>
          </button>

          <button
            onClick={() => setShowNotificationsModal(true)}
            className="rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Shield size={24} style={{ color: 'var(--success)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{userProfile && (userProfile.rewards || userProfile.points || 0)}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Rewards</p>
          </button>
        </div>


        {/* Wallet Card */}
        <div className="rounded-xl p-4 mb-4 shadow-sm border border-orange-100" style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">👛</span>
              <span className="font-semibold text-base text-orange-800">My Wallet</span>
            </div>
            <button
              onClick={onShowWalletTopup}
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border border-orange-300 text-orange-600 bg-white hover:bg-orange-50"
            >
              + Top Up
            </button>
          </div>
          <p className="text-3xl font-bold mb-1 text-orange-700">
            ₹{typeof walletBalance === 'number' ? walletBalance.toFixed(2) : '0.00'}
          </p>
          <p className="text-orange-400 text-xs mb-3">Available Balance</p>
          <button
            onClick={onShowWalletTransactions}
            className="flex items-center gap-1 text-orange-500 text-sm hover:text-orange-700 transition-colors"
          >
            View Transactions →
          </button>
        </div>

        {/* Referral Card — shown only when programme is enabled */}
        {referralInfo?.settings?.enabled && (
          <div className="rounded-xl p-4 mb-4 shadow-sm border border-purple-100" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <Gift size={20} className="text-purple-600" />
              <span className="font-semibold text-purple-800 text-base">Refer &amp; Earn</span>
            </div>

            {/* Benefits */}
            <div className="flex gap-3 mb-3">
              <div className="flex-1 bg-white rounded-lg p-2 text-center border border-purple-100">
                <p className="text-xs text-gray-500 mb-0.5">You earn</p>
                <p className="font-bold text-purple-700 text-sm">
                  {referralInfo.settings.reward_type === 'wallet'
                    ? `₹${referralInfo.settings.reward_amount} wallet`
                    : `₹${referralInfo.settings.reward_amount} coupon`}
                </p>
              </div>
              <div className="flex-1 bg-white rounded-lg p-2 text-center border border-purple-100">
                <p className="text-xs text-gray-500 mb-0.5">Friend earns</p>
                <p className="font-bold text-green-600 text-sm">
                  {referralInfo.settings.referee_reward_type === 'wallet'
                    ? `₹${referralInfo.settings.referee_reward_amount} wallet`
                    : `₹${referralInfo.settings.referee_reward_amount} coupon`}
                </p>
              </div>
              {referralInfo.settings.min_order_amount > 0 && (
                <div className="flex-1 bg-white rounded-lg p-2 text-center border border-purple-100">
                  <p className="text-xs text-gray-500 mb-0.5">Min order</p>
                  <p className="font-bold text-gray-600 text-sm">₹{referralInfo.settings.min_order_amount}</p>
                </div>
              )}
            </div>

            {/* Your referral code */}
            <p className="text-xs text-purple-600 font-medium mb-1">Your referral code</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 bg-white border-2 border-dashed border-purple-300 rounded-lg px-3 py-2 font-mono font-bold text-purple-700 text-center tracking-widest text-lg">
                {referralInfo.referral_code || '—'}
              </div>
              <button
                onClick={() => copyReferralCode(referralInfo.referral_code)}
                className="p-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all flex-shrink-0"
                title="Copy code"
              >
                {referralCodeCopied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            {/* Stats row */}
            {referralInfo.stats && (
              <div className="flex gap-3 mb-3">
                {[
                  { label: 'Total Referred', value: referralInfo.stats.total, color: 'text-purple-700' },
                  { label: 'Pending',         value: referralInfo.stats.pending, color: 'text-yellow-600' },
                  { label: 'Rewarded',        value: referralInfo.stats.rewarded, color: 'text-green-600' },
                ].map(s => (
                  <div key={s.label} className="flex-1 text-center">
                    <p className={`font-bold text-base ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {referralInfo.stats?.total_earned > 0 && (
              <p className="text-xs text-green-700 font-medium mb-3">
                🎉 Total earned: ₹{parseFloat(referralInfo.stats.total_earned).toFixed(2)}
              </p>
            )}

            {/* Apply a friend's code (if not already referred) */}
            {!referralInfo.already_referred && (
              <div className="mt-1">
                <p className="text-xs text-gray-500 mb-1">Have a friend's code? Enter it below:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    maxLength={8}
                    placeholder="Enter code"
                    value={referralInput}
                    onChange={e => setReferralInput(e.target.value.toUpperCase())}
                    className="flex-1 border border-purple-200 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-purple-400 bg-white"
                  />
                  <button
                    onClick={handleApplyReferral}
                    disabled={applyingReferral || !referralInput.trim()}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-all"
                  >
                    {applyingReferral ? '...' : 'Apply'}
                  </button>
                </div>
                {referralMsg && (
                  <p className={`text-xs mt-1.5 font-medium ${referralMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {referralMsg.text}
                  </p>
                )}
              </div>
            )}

            {/* Approval status note */}
            {!referralInfo.settings.auto_approve && (
              <p className="text-xs text-gray-400 mt-2">
                ℹ️ Rewards are credited after admin approval.
              </p>
            )}
          </div>
        )}

        {/* Delivery Addresses */}
      <div className={`${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'} rounded-xl shadow-sm p-4 mb-3`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <MapPin size={20} className="text-blue-600" />
            Delivery Addresses
          </h3>
          <button
            onClick={() => {
              setEditingAddress(null);
              setShowAddAddress(true);
            }}
            className="text-blue-600 text-sm font-medium hover:underline"
          >
            + Add New
          </button>
        </div>

        {(() => {
          const validAddrs = (deliveryAddresses || []).filter(a => a.address && a.address.trim());
          return validAddrs.length > 0 ? (
          <div className="space-y-3">
            {validAddrs.map((addr) => (
              <div
                key={addr.id}
                className={`border-2 rounded-xl p-3 transition-all ${
                  addr.isDefault
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{addr.label}</span>
                    {addr.isDefault && (
                      <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingAddress(addr);
                        setShowAddAddress(true);
                      }}
                      className="text-blue-600 text-sm hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAddress(addr.id)}
                      className="text-red-600 text-sm hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-1">{addr.address}</p>
                <p className="text-sm text-gray-600">
                  {addr.city}, {addr.state} - {addr.pincode}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Phone: {addr.phone}
                </p>

                {!addr.isDefault && (
                  <button
                    onClick={() => handleSetDefaultAddress(addr.id)}
                    className="text-blue-600 text-sm font-medium mt-2 hover:underline"
                  >
                    Set as Default
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-4">
            No delivery addresses added yet
          </p>
        );
        })()}
      </div>


        {/* Menu Items */}

        <div className="space-y-3">
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">My Orders</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-green-600" />
              <span className="font-medium text-gray-700">My Subscriptions</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button
            onClick={() => setShowFavorites(true)}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Heart size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Favorites</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowNotificationsModal(true)}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Notifications</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowPaymentMethods(true)}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Payment Methods</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
          {appSettings.securityAlertsEnabled  && (
          <button 
            onClick={() => setShowSecuritySettings(true)}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-blue-600" />
              <span className="font-medium text-gray-700">Security & Privacy</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
          )}
    
          {userProfile?.is_admin && (
            <>
              <button
                onClick={() => setShowPWAScreen(true)}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md p-4 text-left flex items-center justify-between hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <Smartphone size={20} className="text-white" />
                  <div>
                    <span className="font-medium text-white block">App Settings (PWA)</span>
                    <span className="text-xs text-indigo-100">Install, offline & notifications</span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-white" />
              </button>

              <button
                onClick={() => setShowQRScreen(true)}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-md p-4 text-left flex items-center justify-between hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <QrCode size={20} className="text-white" />
                  <div>
                    <span className="font-medium text-white block">QR Code</span>
                    <span className="text-xs text-emerald-100">Share our website</span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-white" />
              </button>
            </>
          )}
          
          <button
            onClick={() => setActiveTab('help')}
            className={`w-full rounded-xl shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}> 
            <div className="flex items-center gap-3">
              <MessageCircle size={20} className="text-gray-600" />
              <span className="font-medium text-gray-700">Help & Support</span>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

        </div>

        {userProfile?.is_admin && (
        <div className={`${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'} rounded-xl shadow-sm p-4 mt-3`}>
          <div className="flex items-center gap-2 mb-1">
            <Palette size={20} className="text-blue-600" />
            <h3 className="font-semibold text-gray-800">Appearance</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">Choose the look you want. Your selection is saved on this device.</p>

          <div className="space-y-3">
            {themeOptions.map((themeOption) => {
              const ThemeIcon = themeOption.icon;
              const isSelected = appTheme === themeOption.id;

              return (
                <button
                  key={themeOption.id}
                  type="button"
                  onClick={() => onChangeTheme && onChangeTheme(themeOption.id)}
                  className={`w-full rounded-2xl border p-3 text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-100 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${themeOption.previewClass} flex items-center justify-center shadow-sm flex-shrink-0`}>
                        <ThemeIcon size={19} className={themeOption.previewIconClass} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800">{themeOption.label}</p>
                        <p className="text-sm text-gray-500">{themeOption.description}</p>
                      </div>
                    </div>

                    {isSelected && (
                      <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                        <Check size={16} />
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <span className={`h-3 rounded-full bg-gradient-to-r ${themeOption.previewClass}`}></span>
                    <span className="h-3 rounded-full bg-white border border-gray-200"></span>
                    <span className="h-3 rounded-full bg-gray-100 border border-gray-200"></span>
                    <span className="h-3 rounded-full bg-blue-50 border border-blue-200"></span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* App Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">Version {appSettings?.version || '1.0.0'}</p>
          <p className="text-xs text-gray-400 mt-1">© {new Date().getFullYear()} {appSettings?.appName || 'Milk Delivery App'}</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;